import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Table, Btn, Modal, Input, Select, Textarea } from "@/components/UI";
import { Plus, FileText, Trash2, Pencil, ChevronDown, ChevronRight, Calculator, Send, Check, X, MailCheck, Mail, Link2 } from "lucide-react";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import { openPdfPreview } from "@/lib/pdfPreview";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { usePagination, ListPagination } from "@/components/ListPagination";

// ---- Email button for quotations (parallel to Telegram) ----
function QuoteEmailBtn({ q, customers, onSent }) {
  const [busy, setBusy] = React.useState(false);
  const [ready, setReady] = React.useState(null);
  React.useEffect(() => {
    if (ready !== null) return;
    api.get("/system/email-config").then(r => setReady(!!r.configured)).catch(() => setReady(false));
  }, [ready]);
  const cust = customers.find(c => c.id === q.customer_id);
  const hasEmail = !!cust?.email;
  const disabled = busy;
  const title = !hasEmail ? "Bu müşterinin kayıtlı e-posta adresi bulunmamaktadır."
    : ready === false ? "E-posta servisi henüz yapılandırılmamıştır."
    : "E-posta ile PDF gönder";
  const send = async () => {
    if (!hasEmail) { alert("Bu müşterinin kayıtlı e-posta adresi bulunmamaktadır."); return; }
    if (ready === false) { alert("E-posta servisi henüz yapılandırılmamıştır. Sistem Ayarlarından e-posta bağlantısını tamamlayınız."); return; }
    setBusy(true);
    try { await api.post(`/quotations/${q.id}/send-email`, {}); alert(`✓ E-posta gönderildi: ${q.quote_no} → ${cust.email}`); onSent?.(); }
    catch (e) { alert(e.response?.data?.detail || "E-posta gönderilemedi."); }
    finally { setBusy(false); }
  };
  return (
    <Btn size="sm" variant="secondary" disabled={disabled} onClick={send} title={title} data-testid={`quote-email-${q.id}`}>
      <Mail size={12}/>{busy ? " …" : " E-posta"}
    </Btn>
  );
}

const defaultCalcParams = () => ([
  { name: "İç Maliyet", value: 0, auto: true },
  { name: "İşçilik", value: 0 },
  { name: "Ambalaj", value: 0 },
  { name: "Etiket", value: 0 },
  { name: "Kutu", value: 0 },
]);
const emptyLine = () => ({
  product_name: "", description: "", project_no: "",
  unit_price: 0, currency: "EUR", kdv: false, otv: false,
  ml: 0, cost_per_kg: 0, density: 1.0, unit_internal_cost: 0,
  calc_params: defaultCalcParams(),
});
const emptyQuote = () => ({ customer_id: "", customer_name: "", customer_address: "", lines: [emptyLine()], notes: "" });

// -------- Status badges --------
const SendBadge = ({ sent }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${sent ? "bg-emerald-100 text-emerald-700 ring-emerald-600/20" : "bg-yellow-100 text-yellow-800 ring-yellow-600/20"}`}>
    {sent ? "Müşteriye Gönderildi" : "Gönderilmedi"}
  </span>
);
const ApprovalBadge = ({ status }) => {
  const s = (status || "PENDING").toUpperCase();
  const cfg = s === "APPROVED" ? { l: "Onaylandı", c: "bg-emerald-100 text-emerald-700 ring-emerald-600/20" }
    : s === "REJECTED" ? { l: "Onaylanmadı", c: "bg-red-100 text-red-700 ring-red-600/20" }
    : { l: "Beklemede", c: "bg-yellow-100 text-yellow-800 ring-yellow-600/20" };
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cfg.c}`}>{cfg.l}</span>;
};

export function QuotationsPage() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rdSamples, setRdSamples] = useState([]);
  const [projectFormulas, setProjectFormulas] = useState([]);  // Iter217 — for line-level project priority
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState(emptyQuote());
  const [filteredRows, setFilteredRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [sending, setSending] = useState({});
  const visibleRows = filteredRows.length > 0 || rows.length === 0 ? filteredRows : rows;
  const pg = usePagination(visibleRows, "quotations", 100);

  const load = useCallback(() => Promise.all([api.get("/quotations"), api.get("/customers"), api.get("/rd-samples"), api.get("/formulas")])
    .then(([qs, c, s, fo]) => { setRows(qs); setCustomers(c); setRdSamples(s); setProjectFormulas(fo || []); }), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setQ(emptyQuote()); setExpanded({ 0: true }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setQ({ ...emptyQuote(), ...r, lines: (r.lines && r.lines.length ? r.lines.map(l => ({ ...emptyLine(), ...l, calc_params: l.calc_params || defaultCalcParams() })) : [emptyLine()]) }); setExpanded({}); setOpen(true); };
  const save = async () => {
    if (!q.customer_id && !q.customer_name) { alert("Müşteri seçin"); return; }
    if (q.lines.length === 0 || !q.lines.some(l => l.product_name)) { alert("En az bir ürün satırı girin"); return; }
    try {
      if (editing) await api.put(`/quotations/${editing.id}`, q);
      else await api.post("/quotations", q);
      setOpen(false); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const del = async (id) => { if (window.confirm("Bu teklif arşivlensin mi?")) { try { await api.del(`/quotations/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } } };
  const downloadPdf = async (id, quote_no, customer_name) => {
    try {
      const blob = await api.getBlob(`/quotations/${id}/pdf`);
      const safe = (customer_name || "musteri").replace(/[^A-Za-z0-9]+/g, "-");
      openPdfPreview(blob, `Fiyat-Teklifi_${quote_no}_${safe}.pdf`);
    } catch { alert("PDF alınamadı"); }
  };
  const markSent = async (id, via = "MANUAL") => {
    try { await api.post(`/quotations/${id}/mark-sent`, { sent_via: via }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const sendTelegram = async (r) => {
    if (!r.customer_id) { alert("Bu teklifte müşteri seçili değil."); return; }
    setSending(prev => ({ ...prev, [r.id]: true }));
    try {
      await api.post(`/quotations/${r.id}/send-telegram`, {});
      load();
      alert(`Teklif Telegram üzerinden gönderildi (${r.quote_no}). Durum otomatik olarak "Müşteriye Gönderildi" oldu.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Telegram gönderimi başarısız.");
    } finally {
      setSending(prev => ({ ...prev, [r.id]: false }));
    }
  };
  const approve = async (id) => { try { await api.post(`/quotations/${id}/approval`, { approval_status: "APPROVED" }); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } };
  const rejectSave = async () => {
    try {
      await api.post(`/quotations/${rejectOpen.id}/approval`, { approval_status: "REJECTED", rejection_reason: rejectReason });
      setRejectOpen(null); setRejectReason(""); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };

  // ---- New-quote modal helpers (kept unchanged apart from removed duplicate name input) ----
  const recalcUnit = (l) => {
    const cost = (parseFloat(l.cost_per_kg) || 0) * (parseFloat(l.density) || 1) * (parseFloat(l.ml) || 0) / 1000;
    return Math.round(cost * 10000) / 10000;
  };
  const updateLineFields = (i, patch) => {
    setQ(prev => {
      const l = [...prev.lines];
      const merged = { ...l[i], ...patch };
      if ("ml" in patch || "cost_per_kg" in patch || "density" in patch) {
        merged.unit_internal_cost = recalcUnit(merged);
        const cp = [...(merged.calc_params || defaultCalcParams())];
        const iidx = cp.findIndex(p => p.auto);
        if (iidx >= 0) cp[iidx] = { ...cp[iidx], value: merged.unit_internal_cost };
        merged.calc_params = cp;
      }
      l[i] = merged;
      return { ...prev, lines: l };
    });
  };
  const onProjectChange = (i, project_no) => {
    const sample = rdSamples.find(s => s.project_no === project_no);
    if (sample) {
      const cost_per_kg = sample.cost?.cost_per_kg_eur ?? sample.cost?.cost_per_kg ?? 0;
      const density = sample.density || 1.0;
      const ml = sample.unit_ml || 100;
      const unit_internal_cost = cost_per_kg * density * ml / 1000;
      const cp = defaultCalcParams();
      cp[0].value = Math.round(unit_internal_cost * 10000) / 10000;
      updateLineFields(i, { project_no, product_name: sample.product_name || sample.name || q.lines[i].product_name, ml, cost_per_kg, density, unit_internal_cost, calc_params: cp });
      setExpanded(prev => ({ ...prev, [i]: true }));
    } else {
      updateLineFields(i, { project_no });
    }
  };
  const updCalcParam = (i, pi, patch) => setQ(prev => {
    const l = [...prev.lines];
    const cp = [...(l[i].calc_params || [])];
    cp[pi] = { ...cp[pi], ...patch };
    l[i] = { ...l[i], calc_params: cp };
    return { ...prev, lines: l };
  });
  const addCalcParam = (i) => setQ(prev => {
    const l = [...prev.lines];
    l[i] = { ...l[i], calc_params: [...(l[i].calc_params || []), { name: "", value: 0 }] };
    return { ...prev, lines: l };
  });
  const removeCalcParam = (i, pi) => setQ(prev => {
    const l = [...prev.lines];
    l[i] = { ...l[i], calc_params: (l[i].calc_params || []).filter((_, j) => j !== pi) };
    return { ...prev, lines: l };
  });
  const calcTotal = (l) => (l.calc_params || []).reduce((s, p) => s + (parseFloat(p.value) || 0), 0);
  const addLine = () => setQ(prev => { const nl = [...prev.lines, emptyLine()]; setExpanded(e => ({ ...e, [nl.length - 1]: true })); return { ...prev, lines: nl }; });
  const removeLine = (i) => setQ(prev => ({ ...prev, lines: prev.lines.filter((_, j) => j !== i) }));

  const onCustomerChange = (id) => {
    const c = customers.find(x => x.id === id);
    if (c) {
      const addr = c.billing_address || (c.delivery_addresses?.[0]?.full_address || c.delivery_addresses?.[0]?.line || c.delivery_addresses?.[0]?.line1) || "";
      setQ(prev => ({ ...prev, customer_id: id, customer_name: c.legal_name || c.name || "", customer_address: addr }));
    } else {
      setQ(prev => ({ ...prev, customer_id: id }));
    }
  };
  const projectOf = (r) => r.project_no || (r.lines || []).find(l => l.project_no)?.project_no || "—";
  const hasTelegram = (r) => {
    const c = customers.find(x => x.id === r.customer_id);
    return !!c?.telegram_chat_id;
  };

  return (
    <div>
      <PageHeader title="Fiyat Teklifleri" description="Müşteriye özel fiyat teklifi oluştur ve PDF olarak paylaş"
        actions={<Btn onClick={openNew} data-testid="new-quote"><Plus size={14}/> Yeni Teklif</Btn>} />
      <FilterAndReportBar
        reportType="quotations" rows={rows} customers={customers}
        statuses={[{value:"draft",label:"Gönderilmedi"},{value:"sent",label:"Gönderildi"},{value:"accepted",label:"Onaylandı"},{value:"rejected",label:"Onaylanmadı"}]}
        dateAccessor={r => r.created_at}
        customerAccessor={r => r.customer_id}
        productAccessor={r => (r.lines||[]).map(l => l.product_name).join(" ")}
        statusAccessor={r => r.status}
        onFiltered={setFilteredRows}
      />
      <Table columns={[
        { label: "Teklif No", render: r => <span className="font-mono text-xs" data-testid={`quote-no-${r.id}`}>{r.quote_no}</span> },
        { label: "Proje No", render: r => <span className="font-mono text-xs text-indigo-700" data-testid={`quote-proj-${r.id}`}>{projectOf(r)}</span> },
        { label: "Müşteri", render: r => r.customer_name || "—" },
        { label: "Tarih", render: r => (r.created_at || "").slice(0, 10) },
        { label: "Durum", render: r => (
          <div className="flex items-center gap-2">
            <SendBadge sent={!!r.sent_to_customer}/>
            {!r.sent_to_customer && (
              <button onClick={() => markSent(r.id, "MANUAL")} className="text-[10px] underline text-slate-500 hover:text-emerald-700" data-testid={`mark-sent-${r.id}`} title="Manuel gönderildi olarak işaretle">Gönderildi işaretle</button>
            )}
          </div>
        )},
        { label: "Teklif Onayı", render: r => (
          <div className="flex items-center gap-2">
            <ApprovalBadge status={r.approval_status}/>
            {r.sent_to_customer && r.approval_status !== "APPROVED" && (
              <button onClick={() => approve(r.id)} className="text-emerald-600 hover:text-emerald-800" data-testid={`approve-quote-${r.id}`} title="Onayla"><Check size={14}/></button>
            )}
            {r.sent_to_customer && r.approval_status !== "REJECTED" && (
              <button onClick={() => { setRejectOpen(r); setRejectReason(""); }} className="text-red-500 hover:text-red-700" data-testid={`reject-quote-${r.id}`} title="Reddet"><X size={14}/></button>
            )}
            {!r.sent_to_customer && <span className="text-[10px] text-slate-400 italic">gönderim sonrası aktif</span>}
          </div>
        )},
        { label: "İşlem", render: r => (
          <div className="flex gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => downloadPdf(r.id, r.quote_no, r.customer_name)} data-testid={`quote-pdf-${r.id}`} title="PDF indir"><FileText size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`quote-edit-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>
            <Btn size="sm" variant="secondary" disabled={!hasTelegram(r) || sending[r.id]} onClick={() => sendTelegram(r)} data-testid={`quote-tg-${r.id}`} title={hasTelegram(r) ? "Telegram ile PDF gönder" : "Müşterinin Telegram bağlantısı yok"}>
              <Send size={12}/>{sending[r.id] ? " Gönderiliyor…" : " Telegram"}
            </Btn>
            <QuoteEmailBtn q={r} customers={customers} onSent={load}/>
            <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`quote-del-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>
          </div>
        )},
      ]} rows={pg.paged} testid="quotations-table"/>
      <ListPagination pagination={pg} testid="quotations-pagination"/>

      {/* ---------- Reject reason modal ---------- */}
      <Modal open={!!rejectOpen} onClose={() => setRejectOpen(null)} title={`Teklifi Reddet — ${rejectOpen?.quote_no || ""}`} size="md">
        <Textarea label="Red Nedeni" value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="quote-reject-reason"/>
        <div className="flex justify-end gap-2 mt-3">
          <Btn variant="secondary" onClick={() => setRejectOpen(null)}>Vazgeç</Btn>
          <Btn variant="danger" onClick={rejectSave} data-testid="quote-reject-confirm">Reddet</Btn>
        </div>
      </Modal>

      {/* ---------- New / Edit quotation modal ---------- */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Teklif Düzenle — ${editing.quote_no}` : "Yeni Fiyat Teklifi"} size="full">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <SmartSuggestInput
            label="Müşteri *"
            value={q.customer_name || (customers.find(c => c.id === q.customer_id)?.legal_name ?? "")}
            selectedId={q.customer_id}
            items={customers}
            displayField="legal_name"
            idField="id"
            secondaryField={(c) => [c.code && `Kod: ${c.code}`, c.tax_id && `VKN: ${c.tax_id}`, c.city].filter(Boolean).join(" · ")}
            placeholder="Müşteri adı yazın veya listeden seçin..."
            onChange={({ value, selectedId }) => {
              if (selectedId) {
                onCustomerChange(selectedId);
              } else {
                // manual free-text customer
                setQ(prev => ({ ...prev, customer_id: "", customer_name: value }));
              }
            }}
            data-testid="quote-cust"
          />
          <div className="col-span-2"><Textarea label="Müşteri Adresi (otomatik)" rows={2} value={q.customer_address} onChange={e => setQ({ ...q, customer_address: e.target.value })} data-testid="quote-cust-address"/></div>
        </div>

        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Ürün Satırları</div>
            <Btn size="sm" variant="secondary" onClick={addLine} data-testid="quote-add-line"><Plus size={12}/> Satır Ekle</Btn>
          </div>
          <div className="grid gap-2 text-[10px] uppercase text-slate-500 font-semibold px-1 mb-1" style={{gridTemplateColumns: "16px 3fr 3fr 2fr 2fr 1fr 40px 40px 32px"}}>
            <div></div>
            <div>Ürün Adı</div>
            <div>Açıklama</div>
            <div>Proje No</div>
            <div>Satış Fiyatı</div>
            <div>Birim</div>
            <div className="text-center">KDV</div>
            <div className="text-center">ÖTV</div>
            <div></div>
          </div>
          {q.lines.map((l, i) => (
            <div key={i} className="mb-3">
              <div className="grid gap-2 bg-white border border-slate-200 rounded p-1 items-center" style={{gridTemplateColumns: "16px 3fr 3fr 2fr 2fr 1fr 40px 40px 32px"}}>
                <button onClick={() => setExpanded({ ...expanded, [i]: !expanded[i] })} className="text-slate-500 hover:text-teal-600" data-testid={`quote-expand-${i}`}>
                  {expanded[i] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                </button>
                <input value={l.product_name} onChange={e => updateLineFields(i, { product_name: e.target.value })} placeholder="Ürün adı" className="px-2 py-1 text-xs border rounded" data-testid={`quote-line-name-${i}`}/>
                <input value={l.description || ""} onChange={e => updateLineFields(i, { description: e.target.value })} placeholder="Kısa açıklama" className="px-2 py-1 text-xs border rounded" data-testid={`quote-line-desc-${i}`}/>
                <div className="min-w-0" data-testid={`quote-line-proj-wrap-${i}`}>
                  <SmartSuggestInput
                    value={l.project_no || ""}
                    selectedId={(() => {
                      const rd = rdSamples.find(s => s.project_no === l.project_no);
                      if (rd) return rd.id;
                      const fx = projectFormulas.find(x => x.project_no === l.project_no);
                      return fx?.id || null;
                    })()}
                    items={(() => {
                      // Iter217: unify rd_samples + formulas into a single project index
                      // keyed by project_no so line-level Proje No SmartSuggest surfaces
                      // BOTH data sources. Customer priority applies uniformly.
                      const seen = new Set();
                      const merged = [];
                      for (const s of rdSamples) {
                        if (!s.project_no || seen.has(s.project_no)) continue;
                        seen.add(s.project_no);
                        merged.push({
                          id: s.id, project_no: s.project_no,
                          product_name: s.product_name || s.name,
                          customer_id: s.customer_id, _source: "rd",
                        });
                      }
                      for (const fx of projectFormulas) {
                        if (!fx.project_no || seen.has(fx.project_no)) continue;
                        seen.add(fx.project_no);
                        merged.push({
                          id: fx.id, project_no: fx.project_no,
                          product_name: fx.product_name || fx.name,
                          customer_id: fx.customer_id, _source: "formula",
                        });
                      }
                      return merged;
                    })()}
                    displayField="project_no"
                    idField="id"
                    secondaryField={(s) => [s.product_name, customers.find(c => c.id === s.customer_id)?.legal_name].filter(Boolean).join(" — ")}
                    priorityFilter={q.customer_id ? (s) => s.customer_id === q.customer_id : null}
                    priorityLabel="Bu müşterinin"
                    placeholder="MP10000"
                    onChange={({ value, item }) => {
                      if (item) {
                        // Iter217: rd_sample → mevcut onProjectChange cost/density fill'i çalıştırır;
                        // formula-based project (rd sample yoksa) → sadece project_no + product_name fill.
                        const rd = rdSamples.find(s => s.project_no === item.project_no);
                        if (rd) {
                          onProjectChange(i, item.project_no);
                        } else {
                          updateLineFields(i, {
                            project_no: item.project_no,
                            product_name: item.product_name || q.lines[i].product_name,
                          });
                        }
                      } else {
                        updateLineFields(i, { project_no: value });
                      }
                    }}
                    data-testid={`quote-line-proj-${i}`}
                  />
                </div>
                <input type="number" step="0.01" value={l.unit_price} onChange={e => updateLineFields(i, { unit_price: parseFloat(e.target.value) || 0 })} className="px-2 py-1 text-xs border rounded" data-testid={`quote-line-price-${i}`}/>
                <select value={l.currency} onChange={e => updateLineFields(i, { currency: e.target.value })} className="px-2 py-1 text-xs border rounded">
                  <option>EUR</option><option>USD</option><option>TRY</option>
                </select>
                <label className="flex justify-center"><input type="checkbox" checked={!!l.kdv} onChange={e => updateLineFields(i, { kdv: e.target.checked })} data-testid={`quote-line-kdv-${i}`}/></label>
                <label className="flex justify-center"><input type="checkbox" checked={!!l.otv} onChange={e => updateLineFields(i, { otv: e.target.checked })} data-testid={`quote-line-otv-${i}`}/></label>
                <button onClick={() => removeLine(i)} className="text-red-500 text-xs" data-testid={`quote-line-del-${i}`}>Sil</button>
              </div>
              {expanded[i] && (
                <div className="ml-6 mt-1 mb-2 border-l-2 border-teal-300 pl-3">
                  <div className="bg-teal-50/50 border border-teal-200 rounded p-2 mb-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-[10px] uppercase text-teal-700 block">Kg Maliyeti</span>
                      <input type="number" step="0.01" value={l.cost_per_kg || 0} onChange={e => updateLineFields(i, { cost_per_kg: parseFloat(e.target.value) || 0 })} className="font-mono px-1 py-0.5 border border-teal-200 rounded w-full" data-testid={`quote-line-cpk-${i}`}/>
                      <span className="text-[9px] text-slate-500">EUR/kg</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-teal-700 block">ML Değeri</span>
                      <input type="number" step="1" value={l.ml || 0} onChange={e => updateLineFields(i, { ml: parseFloat(e.target.value) || 0 })} className="font-mono px-1 py-0.5 border border-teal-200 rounded w-full" data-testid={`quote-line-ml-${i}`}/>
                      <span className="text-[9px] text-slate-500">ml / adet</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-teal-700 block">Yoğunluk</span>
                      <input type="number" step="0.01" value={l.density || 1.0} onChange={e => updateLineFields(i, { density: parseFloat(e.target.value) || 1.0 })} className="font-mono px-1 py-0.5 border border-teal-200 rounded w-full"/>
                      <span className="text-[9px] text-slate-500">g/ml</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-teal-700 block">Birim Maliyet</span>
                      <span className="font-mono font-semibold text-emerald-700 block" data-testid={`quote-line-uic-${i}`}>{(l.unit_internal_cost || 0).toFixed(4)}</span>
                      <span className="text-[9px] text-slate-500">EUR / adet</span>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1"><Calculator size={12}/> Maliyet Hesap Tablosu <span className="text-[9px] text-slate-400 font-normal">(PDF'te görünmez)</span></div>
                      <Btn size="sm" variant="secondary" onClick={() => addCalcParam(i)} data-testid={`quote-line-add-param-${i}`}><Plus size={10}/> Parametre</Btn>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-slate-500">
                        <tr><th className="text-left w-1/2">Parametre</th><th className="text-right w-1/3">Değer (EUR)</th><th className="w-8"></th></tr>
                      </thead>
                      <tbody>
                        {(l.calc_params || []).map((p, pi) => (
                          <tr key={pi} className="border-t border-slate-100">
                            <td className="py-0.5">
                              <input value={p.name} disabled={!!p.auto} onChange={e => updCalcParam(i, pi, { name: e.target.value })}
                                className={`w-full px-1 py-0.5 border rounded ${p.auto ? 'bg-slate-100 text-slate-500' : ''}`}
                                data-testid={`quote-line-param-name-${i}-${pi}`}/>
                            </td>
                            <td className="py-0.5 text-right">
                              <input type="number" step="0.0001" value={p.value} disabled={!!p.auto} onChange={e => updCalcParam(i, pi, { value: parseFloat(e.target.value) || 0 })}
                                className={`w-full px-1 py-0.5 border rounded font-mono text-right ${p.auto ? 'bg-slate-100 text-slate-500' : ''}`}
                                data-testid={`quote-line-param-val-${i}-${pi}`}/>
                            </td>
                            <td className="py-0.5 text-center">
                              {!p.auto && <button onClick={() => removeCalcParam(i, pi)} className="text-red-500 text-xs">✕</button>}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-teal-500 bg-teal-50/40">
                          <td className="py-1 font-semibold text-teal-800">TOPLAM</td>
                          <td className="py-1 text-right font-mono font-bold text-teal-800" data-testid={`quote-line-total-${i}`}>{calcTotal(l).toFixed(4)} EUR</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
          <datalist id="rd-projects">
            {/* iter95: proje seçenekleri arasında ürün + müşteri gösterilecek şekilde zenginleştirildi */}
            {rdSamples.filter(s => s.project_no).map(s => {
              const cust = customers.find(c => c.id === s.customer_id);
              const custName = cust?.legal_name || cust?.name || s.customer_name || "";
              const prod = s.product_name || s.name || "";
              return <option key={s.id} value={s.project_no} label={[prod, custName].filter(Boolean).join(" — ")}/>;
            })}
          </datalist>
        </div>

        <div className="mt-3"><Textarea label="Notlar (PDF'te görünecek)" rows={3} value={q.notes} onChange={e => setQ({ ...q, notes: e.target.value })} data-testid="quote-notes"/></div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-quote">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}
